/**
 * Manager Panel — Super Admin only, the same rule the API enforces on
 * /api/admin. Anyone else is sent to their main page.
 * Two tabs: "Create" (a user, a team with its Owner) and "Users" (change
 * roles, access and teams, or deactivate someone).
 * Spec: docs/auth-roles-and-access.md.
 */
import type { KeyboardEvent } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { Loader2, UserPlus, Users } from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { useTranslations } from "@/hooks/use-translations";
import { cn } from "@/lib/utils";
import { Card } from "@/components/ui/card";
import { CreateTeamForm } from "@/components/manager/create-team-form";
import { CreateUserForm } from "@/components/manager/create-user-form";
import { UsersList } from "@/components/manager/users-list";

const TABS = [
	{ id: "create", icon: UserPlus },
	{ id: "users", icon: Users },
] as const;
type TabId = (typeof TABS)[number]["id"];

const tabId = (id: TabId) => `manager-tab-${id}`;
const panelId = (id: TabId) => `manager-panel-${id}`;

export function ManagerPanelPage() {
	const { user, isLoading } = useAuth();
	const t = useTranslations("manager");
	// The open tab lives in the URL, so a reload or a link keeps it.
	const [searchParams, setSearchParams] = useSearchParams();
	const selectedIndex = Math.max(
		0,
		TABS.findIndex((tab) => tab.id === searchParams.get("tab")),
	);
	const selected = TABS[selectedIndex];

	if (isLoading) {
		return (
			<div className="flex min-h-screen items-center justify-center bg-bg-canvas">
				<Loader2 size={24} className="animate-spin text-text-tertiary" />
			</div>
		);
	}

	// Role guard (App.tsx checks too) — everyone else goes to their main page
	if (!user || user.siteRole !== "super_admin") {
		return <Navigate to="/" replace />;
	}

	const select = (id: TabId) => setSearchParams({ tab: id }, { replace: true });

	// Arrow keys move between tabs, as on the Connectors page.
	const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
		const step = e.key === "ArrowRight" ? 1 : e.key === "ArrowLeft" ? -1 : 0;
		if (!step) return;
		e.preventDefault();
		const next = TABS[(selectedIndex + step + TABS.length) % TABS.length];
		select(next.id);
		document.getElementById(tabId(next.id))?.focus();
	};

	return (
		// Same layout as the Connectors page: title, tab bar, one card per tab.
		<div className="min-h-screen p-6 bg-bg-canvas">
			<div className="max-w-7xl mx-auto">
				<div className="mb-8">
					<h1 className="text-2xl font-semibold text-text-primary">{t("title")}</h1>
					<p className="text-text-tertiary">{t("subtitle")}</p>
				</div>

				<div
					role="tablist"
					aria-label={t("title")}
					onKeyDown={handleKeyDown}
					className="mb-4 flex gap-1 overflow-x-auto rounded-xl border border-border-subtle bg-bg-surface p-1 scrollbar-thin"
				>
					{TABS.map((tab) => {
						const isSelected = tab.id === selected.id;
						const Icon = tab.icon;
						return (
							<button
								key={tab.id}
								type="button"
								role="tab"
								id={tabId(tab.id)}
								aria-selected={isSelected}
								aria-controls={panelId(tab.id)}
								tabIndex={isSelected ? 0 : -1}
								onClick={() => select(tab.id)}
								className={cn(
									// Equal share of the bar for every tab; never narrower than its label.
									"group flex min-w-max flex-1 items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring focus-visible:ring-offset-2",
									// Same highlight as the active sidebar item (GUIDE rule 6).
									isSelected
										? "bg-nav-active-bg font-semibold text-text-primary"
										: "font-medium text-text-secondary hover:bg-bg-canvas hover:text-text-primary",
								)}
							>
								<Icon
									size={18}
									className={
										isSelected
											? "text-text-primary"
											: "text-text-secondary group-hover:text-text-primary"
									}
								/>
								{t(`tabs.${tab.id}`)}
							</button>
						);
					})}
				</div>

				<div role="tabpanel" id={panelId(selected.id)} aria-labelledby={tabId(selected.id)}>
					<Card>
						{selected.id === "create" ? (
							<>
								<section className="p-5">
									<CreateUserForm />
								</section>
								<section className="border-t border-border-subtle p-5">
									<CreateTeamForm />
								</section>
							</>
						) : (
							<UsersList />
						)}
					</Card>
				</div>
			</div>
		</div>
	);
}
